import { Box, Image } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import type { ImageFormat, Picture } from "~/core";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type LazyDirectusImageProps = {
  picture: Picture;
  format?: ImageFormat;
};

export const LazyDirectusImage: FC<LazyDirectusImageProps> = ({
  picture,
  format = "webp",
}) => {
  const { getDirectusImage } = useDirectus();
  const ref = useRef<HTMLDivElement | null>(null);

  const [mSrc, setMSrc] = useState<string>();
  const [mdSrc, setMdSrc] = useState<string>();
  const [dSrc, setDSrc] = useState<string>();

  const mobileId = picture?.image?.id ?? null;
  const tabletId = picture?.tablet_image?.id ?? mobileId;
  const desktopId = picture?.desktop_image?.id ?? tabletId;

  const mobileWidth = picture?.mobile_image_width ?? undefined;
  const mobileHeight = picture?.mobile_image_height ?? undefined;
  const tabletWidth = picture?.tablet_image_width ?? mobileWidth;
  const tabletHeight = picture?.tablet_image_height ?? mobileHeight;
  const desktopWidth = picture?.desktop_image_width ?? tabletWidth;
  const desktopHeight = picture?.desktop_image_height ?? tabletHeight;

  const altText = picture?.alternative_text ?? "";

  const getSrc = (
    fileId: string | null,
    width?: number,
    height?: number,
  ): string | undefined => {
    if (!fileId) return undefined;
    return getDirectusImage(fileId, {
      width: width ? width * 2 : undefined,
      height: height ? height * 2 : undefined,
      format,
      quality: 85,
    });
  };

  useEffect(() => {
    const node = ref.current;
    if (!node || !picture || mSrc) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        setMSrc(getSrc(mobileId, mobileWidth, mobileHeight));
        setMdSrc(getSrc(tabletId, tabletWidth, tabletHeight));
        setDSrc(getSrc(desktopId, desktopWidth, desktopHeight));

        observer.disconnect();
      });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    picture,
    mSrc,
    mobileId,
    mobileWidth,
    mobileHeight,
    tabletId,
    tabletWidth,
    tabletHeight,
    desktopId,
    desktopWidth,
    desktopHeight,
    format,
  ]);

  if (!picture) return null;

  return (
    <Box ref={ref}>
      {mSrc && (
        <Image
          value={mSrc}
          mdValue={mdSrc}
          dValue={dSrc}
          image={{ alt: altText }}
          maxWidth={mobileWidth}
          maxHeight={mobileHeight}
          md={{ maxWidth: tabletWidth, maxHeight: tabletHeight }}
          d={{ maxWidth: desktopWidth, maxHeight: desktopHeight }}
        />
      )}
    </Box>
  );
};
